import {
  ModeleSavClient,
  ModeleSavUtilisateur,
  SavOrigineCode,
  SavReferentielType,
  SavStatutCode,
} from './Models'

// Libellés des comportements fixes du code (spec 4.5)
export const SAV_COMPORTEMENT_LIBELLES: Record<string, string> = {
  TELEPHONE: 'Téléphone',
  MAIL: 'Mail',
  COMPTOIR: 'Comptoir',
  PLATEFORME: 'Plateforme',
  GENERIQUE: 'Générique',
  AVOIR: 'Avoir',
  ECHANGE: 'Échange',
  SANS_EFFET_COMPTABLE: 'Sans effet comptable',
  REMISE_EN_STOCK: 'Remise en stock',
  DESTRUCTION: 'Destruction',
  RENVOI_CLIENT: 'Renvoi client',
  AUTRE: 'Autre',
}

// Colonne « Option » : texte fixe déduit du comportement
export const SAV_COMPORTEMENT_OPTIONS: Record<string, string> = {
  TELEPHONE: 'N° appelant, interlocuteur, date et heure',
  MAIL: 'Boîte de réception, expéditeur, import du mail',
  COMPTOIR: 'Pièce rapportée possible : dossier créé en Reçu',
  PLATEFORME: 'Plateforme et n° de commande',
  GENERIQUE: 'Interlocuteur et commentaire',
  AVOIR: "N° d'avoir ATEMO, montant et date obligatoires",
  ECHANGE: 'Commande ATEMO (n° saisi) ou demande télévente AC03 ; passage par le statut Commande',
  SANS_EFFET_COMPTABLE: 'Aucune donnée comptable',
  REMISE_EN_STOCK: 'Tâche créée pour la Logistique (saisie dans ATEMO)',
  DESTRUCTION: '—',
  RENVOI_CLIENT: 'Réexpédition au client',
  AUTRE: '—',
}

export const SAV_REFERENTIEL_ONGLETS: Array<{
  type: SavReferentielType
  titre: string
  ajouter: string
  aide: string
}> = [
  {
    type: 'sources',
    titre: 'Sources',
    ajouter: 'Ajouter une source',
    aide: 'Canal par lequel la réclamation arrive. Le comportement fixe les champs demandés à la saisie.',
  },
  {
    type: 'motifs',
    titre: 'Motifs',
    ajouter: 'Ajouter un motif',
    aide: 'Motif de la réclamation, par pièce. Le retour par défaut reste modifiable à la qualification.',
  },
  {
    type: 'solutions',
    titre: 'Solutions',
    ajouter: 'Ajouter une solution',
    aide: 'Solution retenue à la clôture. Le comportement fixe les données exigées.',
  },
  {
    type: 'destinations',
    titre: 'Destinations',
    ajouter: 'Ajouter une destination',
    aide: 'Devenir de la pièce après le contrôle labo. La destination est présélectionnée selon le résultat.',
  },
]

// Badges de statut (maquette 0.7)
export const SAV_STATUTS: Record<SavStatutCode, {libelle: string; fond: string; texte: string}> = {
  OUVERT: {libelle: 'Ouvert', fond: '#F1F1F4', texte: '#78829D'},
  RETOUR_ATTENDU: {libelle: 'Retour attendu', fond: '#FFF8DD', texte: '#B58A00'},
  RECU: {libelle: 'Reçu', fond: '#E9F3FF', texte: '#1B84FF'},
  EN_CONTROLE: {libelle: 'En contrôle', fond: '#1B84FF', texte: '#FFFFFF'},
  A_SOLDER: {libelle: 'À solder', fond: '#DFF6F8', texte: '#00838F'},
  COMMANDE: {libelle: 'Commande', fond: '#F3E8FF', texte: '#6B21A8'},
  CLOS_ACCEPTE: {libelle: 'Clos accepté', fond: '#00B2C2', texte: '#FFFFFF'},
  CLOS_REFUSE: {libelle: 'Clos refusé', fond: '#F8285A', texte: '#FFFFFF'},
  ANNULE: {libelle: 'Annulé', fond: '#FFEEF3', texte: '#F8285A'},
}

export const SAV_STATUTS_FINAUX: Array<SavStatutCode> = ['CLOS_ACCEPTE', 'CLOS_REFUSE', 'ANNULE']

export const SAV_ORIGINES: Record<SavOrigineCode, string> = {
  KOENA_CMD: 'Commande KOENA',
  KOENA_B2B: 'Catalogue B2B',
  ATEMO: 'Vente ATEMO',
  PLATEFORME: 'Plateforme',
  HISTO_ODOO: 'Historique ODOO',
  LIBRE: 'Libre',
}

// Icône du canal d'arrivée, selon le comportement de la source
export const SAV_SOURCE_ICONES: Record<string, string> = {
  TELEPHONE: 'bi-telephone',
  MAIL: 'bi-envelope',
  COMPTOIR: 'bi-shop',
  PLATEFORME: 'bi-globe',
  GENERIQUE: 'bi-chat-dots',
}

export const nomSavClient = (
  client?: ModeleSavClient | null,
  contactNom?: string | null
): string => {
  if (client?.generique && contactNom) return contactNom
  if (!client) return ''
  return (client.nomSociete || `${client.prenom ?? ''} ${client.nom ?? ''}`).trim()
}

export const nomSavUtilisateur = (utilisateur?: ModeleSavUtilisateur | null): string =>
  utilisateur ? `${utilisateur.prenom ?? ''} ${utilisateur.nom ?? ''}`.trim() : ''

// Écran ouvert par le n° SAV (maquette 1.5)
export const urlSavDossier = (dossier: {id: number; statut: {code: SavStatutCode}}): string => {
  if (dossier.statut.code === 'OUVERT') return `/sav/dossiers/${dossier.id}/qualification`
  if (dossier.statut.code === 'A_SOLDER' || dossier.statut.code === 'COMMANDE')
    return `/sav/dossiers/${dossier.id}/solution`
  return `/sav/dossiers/${dossier.id}/fiche`
}

export const formatSavDate = (date?: string | null, avecHeure = false): string => {
  if (!date) return ''
  const d = new Date(date)
  return avecHeure
    ? d.toLocaleString('fr-FR', {dateStyle: 'short', timeStyle: 'short'})
    : d.toLocaleDateString('fr-FR')
}

export const formatSavMontant = (montant?: string | number | null): string =>
  montant === null || montant === undefined || montant === ''
    ? '—'
    : `${Number(montant).toLocaleString('fr-FR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })} €`
